import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BK = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${BK}${u}`);

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("paul@cass-online.co.uk");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [c, setC] = useState(null);

  useEffect(() => { axios.get(`${BK}/api/public/home`).then((r) => setC(r.data)).catch(() => setC({})); }, []);
  useEffect(() => { if (user) navigate(user.role === "engager" ? "/engager/board" : "/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u?.role === "engager" ? "/engager/board" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const h = c || {};
  const navy = h.primary_color || "#0B1A2F";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between text-white p-14 relative grain overflow-hidden" style={{ background: navy }} data-testid="login-brand-panel">
        {abs(h.bg_url) && <><img src={abs(h.bg_url)} alt="" className="absolute inset-0 w-full h-full object-cover" /><div className="absolute inset-0" style={{ background: `${navy}cc` }} /></>}
        <div className="relative z-10">
          {abs(h.logo_url)
            ? <img src={abs(h.logo_url)} alt="logo" className="h-12" data-testid="login-logo" />
            : (h.brand_name && <div className="font-alt text-3xl font-extrabold" data-testid="login-brand-name">{h.brand_name}</div>)}
          {h.brand_subtitle && <div className="text-slate-400 text-sm mt-1" data-testid="login-brand-subtitle">{h.brand_subtitle}</div>}
        </div>
        <div className="relative z-10">
          {h.headline && <h1 className="font-display text-5xl leading-tight font-bold" data-testid="login-headline">{h.headline}</h1>}
          {h.tagline && <p className="text-slate-400 mt-4 text-lg" data-testid="login-tagline">{h.tagline}</p>}
        </div>
        {h.footer_note && <div className="relative z-10 text-slate-500 text-xs" data-testid="login-footer-note">{h.footer_note}</div>}
      </div>
      <div className="flex items-center justify-center p-8 bg-cass-bone">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            {h.signin_heading && <h2 className="font-alt text-2xl font-bold text-cass-navy">{h.signin_heading}</h2>}
            {h.signin_sub && <p className="text-sm text-cass-slate mt-1">{h.signin_sub}</p>}
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-cass-navy hover:bg-cass-navy/90 text-white">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <button type="button" onClick={async () => {
            if (!email) return toast.error("Enter your email first");
            try { await axios.post(`${BK}/api/forgot-password`, { email }); } catch { /* ignore */ }
            toast.success("If that email is registered, a reset link is on its way");
          }} className="text-xs text-cass-teal underline w-full text-center" data-testid="login-forgot">Forgot password?</button>
        </form>
      </div>
    </div>
  );
}
